import { Form, FormInstance, Select } from 'antd';
import { Store } from 'antd/lib/form/interface';
import React, { useMemo } from 'react';

import { useMutation } from '@common/hooks';
import { datetime } from '@common/services/datetime';
import { ComplianceItemFilesListV2 } from '@onboarding/components/compliance-item-files-list';
import { ComplianceItemInputExpirationDate } from '@onboarding/components/compliance-item-input-expiration-date';
import { ComplianceItemV2Type } from '@onboarding/graphql';
import { UpdateNurseAssessmentMutation } from 'works/legacy/graphql/nurse-assessment';
import { formatToYearMonthDay } from 'works/legacy/lib/date-helper';
import { addErrorsToForm } from 'works/legacy/lib/form-helpers';
import { createMessage } from 'works/legacy/lib/message-helper';

type Props = {
  form: FormInstance;
  complianceItem: ComplianceItemV2Type;
  refetchQueries: string[];
  onDeleted?: () => void;
  onSubmit?: () => void;
  onSuccess?: () => void;
  onError?: () => void;
};

export function ComplianceItemUpdateFormAssessment({
  form,
  complianceItem,
  refetchQueries,
  onDeleted,
  onSubmit,
  onSuccess,
  onError,
}: Props) {
  const { detailItem } = complianceItem;

  const errorMessage = () =>
    createMessage('error', 'Failed to update assessment. Please try again.');

  const [updateNurseAssessment, { loading: formSubmitting }] = useMutation(
    UpdateNurseAssessmentMutation,
    {
      refetchQueries,
      onCompleted: (response) => {
        if (response?.updateNurseAssessment.errors) {
          addErrorsToForm(form, response.updateNurseAssessment.errors);
          errorMessage();
          onError?.();
        } else {
          onSuccess?.();
          createMessage('success', 'Assessment has been successfully updated.');
        }
      },
      onError: () => {
        errorMessage();
        onError?.();
      },
    }
  );

  const initialValues = useMemo(
    () => ({
      completedOn: detailItem.completedOn && datetime(detailItem.completedOn),
      expiresOn: detailItem.expiresOn && datetime(detailItem.expiresOn),
      issuerId: complianceItem.issuedAssessment?.issuer?.id,
    }),
    [complianceItem, detailItem]
  );

  if (!detailItem) {
    return null;
  }

  const onFinish = (values: Store) => {
    if (formSubmitting) return;

    onSubmit?.();

    updateNurseAssessment({
      variables: {
        id: complianceItem.id,
        input: {
          completedOn: formatToYearMonthDay(values.completedOn),
          expiresOn: formatToYearMonthDay(values.expiresOn),
        },
        issuerId: values.issuerId,
        files: values.files?.fileList ?? [],
      },
    });
  };

  return (
    <Form
      onFinish={onFinish}
      layout="vertical"
      form={form}
      initialValues={initialValues}
    >
      <ComplianceItemFilesListV2
        refetchQueries={refetchQueries}
        complianceItemId={complianceItem.id}
        complianceItemStateApprovedAt={complianceItem.stateApprovedAt}
        attachmentList={detailItem.detailItemAttachments}
        onDeleted={onDeleted}
        formContent={
          <>
            {detailItem.name && (
              <Form.Item label="AssessmentName">{detailItem.name}</Form.Item>
            )}

            <ComplianceItemInputExpirationDate
              expirationType={complianceItem.expirationType}
            />

            <Form.Item label="Accepted Issuer(s)">
              {complianceItem.assessmentIssuers
                ?.map((issuer) => issuer.name)
                .join(', ')}
            </Form.Item>

            <Form.Item
              label="Issuer Selected"
              name="issuerId"
              rules={[{ required: true, message: 'Please select an issuer' }]}
            >
              <Select size="large">
                {complianceItem.assessmentIssuersToBeSelected?.map((issuer) => (
                  <Select.Option key={issuer.id} value={issuer.id}>
                    {issuer.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </>
        }
      />
    </Form>
  );
}
